import { Subscriptions } from "@bezoned/shared/features/subscriptions/subscriptions.constants.js";
import { Toaster } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs";
import { useEffect, useRef } from "react";

import { getErrorMessage } from "../features/fluid/error-codes.js";
import { DesktopApp } from "../features/notifications/desktop-app/desktop-app.js";
import { FeaturebaseChangelogDialog } from "../features/notifications/featurebase-changelog-dialog.js";
import { IntroductionVideo } from "../features/notifications/intro-video/introduction-video.js";
import { useLobbyDialog } from "../features/notifications/use-lobby-dialog.js";
import { TenantPermissionUpgradeBanner } from "../features/onboarding/tenant-permission-upgrade-banner.js";
import { ActiveTrialBanner } from "../features/subscription/active-trial-banner.js";
import { useSubscription } from "../features/subscription/subscription.context.js";
import { TeamEditList } from "../features/team/team-edit/team-edit-list.js";
import { getTeamCreationJobsQueryOptions } from "../features/team/team-list/team-creation-jobs.query.js";
import { TeamListSideMenu } from "../features/team/team-list/team-list-side-menu.js";
import { TeamList } from "../features/team/team-list/team-list.js";
import { buildTeamLobbyRenderModel } from "../features/team/team-list/team-lobby-render-model.js";
import { getTeamsGetQueryOptions } from "../features/team/team-list/teams-get.query.js";
import { useToast } from "../hooks/use-toast.js";
import { appChangelogGet } from "../services/api.service.js";

// oxlint-disable complexity
export const ChooseTeamPage = () => {
  const [editMode, setEditMode] = useQueryState(
    "edit",
    parseAsBoolean.withDefault(false)
  );
  const [errorCode, setErrorCode] = useQueryState("error", parseAsString);
  const { toasterId, dispatchFailureToast, dispatchWarningToast } = useToast();
  const { trialExpiresAt, source, planId } = useSubscription();
  const hasShownTeamsQueryErrorToast = useRef(false);

  // Fetch teams data (TanStack Query deduplicates with TeamList's query)
  const {
    data: teamsResponse,
    isError: isTeamsError,
    isSuccess: isTeamsSuccess,
  } = useQuery(getTeamsGetQueryOptions());
  const {
    data: createTeamJobs = [],
    isError: isCreateTeamJobsError,
    isSuccess: isCreateTeamJobsSuccess,
  } = useQuery(getTeamCreationJobsQueryOptions());

  // Fetch changelog data
  const { data: changelog } = useQuery({
    queryFn: () => appChangelogGet(),
    queryKey: ["appChangelogGet"],
  });
  let teamsState: "loading" | "success" | "error" = "loading";
  if (isTeamsError) {
    teamsState = "error";
  } else if (isTeamsSuccess) {
    teamsState = "success";
  }
  let createTeamJobsState: "loading" | "success" | "error" = "loading";
  if (isCreateTeamJobsError) {
    createTeamJobsState = "error";
  } else if (isCreateTeamJobsSuccess) {
    createTeamJobsState = "success";
  }

  const teams = teamsResponse?.teams ?? [];
  const starterSlotConsumed = Boolean(
    planId === Subscriptions.StarterPlan &&
    teamsResponse &&
    teamsResponse.meta.totalOfficeCount +
      teamsResponse.meta.activeCreateOfficeJobCount >
      0
  );

  const createTeamLobbyModel = buildTeamLobbyRenderModel({
    editMode,
    jobs: createTeamJobs,
    teams,
  });

  // Centralized dialog priority
  const {
    activeDialog,
    dismissOnboardingVideo,
    dismissCreateTeam,
    dismissChangelog,
    minimizeDesktopAppPrompt,
    requestOpenVideo,
    requestOpenDesktopAppPrompt,
    showVideoFloatingButton,
    showDesktopAppFloatingButton,
    videoId,
  } = useLobbyDialog({
    changelogId: changelog?.id ?? null,
    changelogPublishedAt: changelog?.publishedAt ?? null,
    createTeamJobsBlockAutoOpen: createTeamLobbyModel.hasAutoOpenBlockingJobs,
    createTeamJobsState,
    editMode,
    teamsCount: isTeamsSuccess && !starterSlotConsumed ? teams.length : 1,
    teamsState,
  });
  const visibleFloatingButtons = [
    showDesktopAppFloatingButton ? "desktop-app" : null,
    showVideoFloatingButton ? "onboarding-video" : null,
  ].filter((button): button is "desktop-app" | "onboarding-video" =>
    Boolean(button)
  );
  const desktopButtonStackIndex = visibleFloatingButtons.indexOf("desktop-app");
  const videoButtonStackIndex =
    visibleFloatingButtons.indexOf("onboarding-video");

  // Show notification based on error code in URL
  useEffect(() => {
    if (errorCode) {
      const message = getErrorMessage(errorCode);
      dispatchWarningToast({ message });
      setErrorCode(null);
    }
  }, [dispatchWarningToast, errorCode, setErrorCode]);

  useEffect(() => {
    if (isTeamsError && !hasShownTeamsQueryErrorToast.current) {
      dispatchFailureToast({
        message:
          "Could not load teams. Dialogs were paused. Please refresh and try again.",
      });
      hasShownTeamsQueryErrorToast.current = true;
      return;
    }

    if (!isTeamsError) {
      hasShownTeamsQueryErrorToast.current = false;
    }
  }, [dispatchFailureToast, isTeamsError]);

  return (
    <>
      {/* Toaster should always be rendered, to ensure we can show it even with isLoading or error set */}
      <Toaster toasterId={toasterId} />

      <div className="flex h-screen flex-col">
        <ActiveTrialBanner
          expiresAt={trialExpiresAt}
          show={source === "trial"}
        />
        <TenantPermissionUpgradeBanner />
        <section className="flex min-h-0 max-w-full flex-1 justify-center overflow-y-auto p-12">
          <div className="flex w-full flex-col">
            <div className="flex w-full justify-end">
              <TeamListSideMenu editMode={editMode} setEditMode={setEditMode} />
            </div>
            <section className="mx-auto w-full max-w-[400px]">
              <header className="mb-12 text-center">
                <h1 className="p-0 text-4xl font-medium">Lobby</h1>
              </header>

              {editMode ? (
                <TeamEditList dispatchFailureToast={dispatchFailureToast} />
              ) : (
                <TeamList
                  createTeamDialogOpen={
                    activeDialog === "create-team" && !starterSlotConsumed
                  }
                  dispatchFailureToast={dispatchFailureToast}
                  onCreateTeamDialogDismiss={dismissCreateTeam}
                  starterSlotConsumed={starterSlotConsumed}
                />
              )}
            </section>
          </div>
        </section>

        {videoId && (
          <IntroductionVideo
            floatingButtonStackIndex={videoButtonStackIndex}
            onDismiss={dismissOnboardingVideo}
            onRequestOpen={requestOpenVideo}
            open={activeDialog === "onboarding-video"}
            showFloatingButton={showVideoFloatingButton}
            videoId={videoId}
          />
        )}

        <DesktopApp
          floatingButtonStackIndex={desktopButtonStackIndex}
          onDismiss={minimizeDesktopAppPrompt}
          onRequestOpen={requestOpenDesktopAppPrompt}
          onViewMore={minimizeDesktopAppPrompt}
          open={activeDialog === "desktop-app"}
          showFloatingButton={showDesktopAppFloatingButton}
        />

        {changelog && (
          <FeaturebaseChangelogDialog
            changelog={changelog}
            onDismiss={dismissChangelog}
            open={activeDialog === "changelog"}
          />
        )}
      </div>
    </>
  );
};
